import React, { useEffect } from 'react';
import useCheckoutState from './hooks/useCheckoutState';
import ChargeStatusMessage from './components/ChargeStatusMessage/ChargeStatusMessage';
import SubscriptionStatusMessage from './components/SubscriptionStatusMessage/SubscriptionStatusMessage';
import { useQuery } from '@tanstack/react-query';
import api from './api';

import CheckoutLoader from './components/CheckoutLoader';
import getSessionId from './lib/getSessionId';
import { submissionKey, paymentKey, subscriptionKey, eventPaymentKey } from './queryKeys';
import getCheckoutPath from './lib/getCheckoutPath';
import getSellerSlug from './services/getSellerSlug';
import getCheckoutSlug from './services/getCheckoutSlug';
import SubmissionStatus from './components/SubmissionStatus';
import { PAGE_TYPES } from './enums/page-types';
import EventConfirmation from './EventConfirmation/EventConfirmation';
import useCheckoutRouter from '@repo/checkout/hooks/useCheckoutRouter';

// for preview, generate a random id
const generateRandomId = () => Math.random().toString(36).substr(2, 9);

export default function CheckoutConfirmation({ paymentId: paymentIdProp }) {
  const router = useCheckoutRouter();

  const { checkout, product, seller, checkoutType, params, isTestingPayments, isPreview } =
    useCheckoutState();

  // previewing via After payment > Confirmation
  const isUninteractivePreview = isPreview && !isTestingPayments;

  const paymentId = isUninteractivePreview ? generateRandomId() : paymentIdProp || params.paymentId;
  const sessionId = isUninteractivePreview ? generateRandomId() : getSessionId();

  const { data: submission, isPending: isLoadingSubmission } = useQuery({
    queryKey: submissionKey(paymentId, sessionId),
    queryFn: () => api.getSubmission(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.FORM && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.FORM && {
        initialData: submissionFallbackValues(checkout),
      }),
  });

  const { data: eventPayment, isPending: isLoadingEventPayment } = useQuery({
    queryKey: eventPaymentKey(paymentId, sessionId),
    queryFn: () => api.getCharge(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.EVENT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.EVENT && {
        initialData: eventChargeFallbackValues(checkout),
      }),
  });

  const { data: payment, isPending: isLoadingPayment } = useQuery({
    queryKey: paymentKey(paymentId, sessionId),
    queryFn: () => api.getCharge(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.CHECKOUT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.CHECKOUT && {
        initialData: chargeFallbackValues(checkout, product),
      }),
  });

  const { data: subscription, isPending: isLoadingSubscription } = useQuery({
    queryKey: subscriptionKey(paymentId, sessionId),
    queryFn: () => api.getSubscription(paymentId, sessionId),
    retry: false,
    enabled: checkout.type === PAGE_TYPES.CHECKOUT && !isUninteractivePreview,
    ...(isUninteractivePreview &&
      checkout.type === PAGE_TYPES.CHECKOUT && {
        initialData: subscriptionFallbackValues(checkout, product),
      }),
  });

  // push back to checkout if no submission is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.FORM &&
      !isLoadingSubmission &&
      !submission &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [checkout, isLoadingSubmission, submission, isUninteractivePreview, router, checkoutType]);

  // push back to checkout if no event payment is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.EVENT &&
      !isLoadingEventPayment &&
      !eventPayment &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [checkout, isLoadingEventPayment, eventPayment, isUninteractivePreview, router, checkoutType]);

  // push back to checkout if no payment or subscription is found
  useEffect(() => {
    if (
      checkout.type === PAGE_TYPES.CHECKOUT &&
      !isLoadingPayment &&
      !isLoadingSubscription &&
      !payment &&
      !subscription &&
      !isUninteractivePreview
    ) {
      const sellerSlug = getSellerSlug(checkout.seller);
      const checkoutSlug = getCheckoutSlug(checkout);
      router.push(getCheckoutPath(sellerSlug, checkoutSlug, checkoutType));
    }
  }, [
    checkout,
    isLoadingPayment,
    isLoadingSubscription,
    payment,
    subscription,
    isUninteractivePreview,
    router,
    checkoutType,
  ]);

  if (checkout.type === PAGE_TYPES.FORM) {
    if (isLoadingSubmission) {
      return <CheckoutLoader />;
    }

    if (!submission) {
      return <CheckoutLoader />;
    }

    return (
      <div className="flex flex-col justify-center items-start rounded w-full self-start">
        <SubmissionStatus submission={submission} preview={isPreview} isConfirmation />
      </div>
    );
  } else if (checkout.type === PAGE_TYPES.EVENT) {
    if (isLoadingEventPayment) {
      return <CheckoutLoader />;
    }

    if (!eventPayment) {
      return <CheckoutLoader />;
    }

    return (
      <div className="flex flex-col justify-center items-start rounded w-full self-start">
        <EventConfirmation payment={eventPayment} isConfirmation />
      </div>
    );
  } else if (checkout.type === PAGE_TYPES.CHECKOUT) {
    if (isLoadingPayment || isLoadingSubscription) {
      return <CheckoutLoader />;
    }

    if (!payment && !subscription) {
      return <CheckoutLoader />;
    }

    if (product.type === 'charge') {
      return (
        <div className="flex flex-col justify-center items-start rounded w-full self-start">
          <ChargeStatusMessage
            status={payment.status}
            email={payment.customer.email}
            customer={payment.customer}
            seller={seller}
            charge={payment}
            checkout={checkout}
            preview={isPreview}
            isConfirmation
          />
        </div>
      );
    }

    if (product.type === 'subscription') {
      return (
        <div className="flex flex-col justify-center items-start rounded w-full self-start">
          <SubscriptionStatusMessage
            subscription={subscription}
            email={subscription.customer.email}
            status={subscription.status}
            seller={seller}
            customer={subscription.customer}
            checkout={checkout}
            preview={isPreview}
            isConfirmation
          />
        </div>
      );
    }

    return '';
  }
}
const submissionFallbackValues = page => ({
  _id: `{Submission ID}`,
  page: page._id,
  createdAt: new Date(),
  locale: page.locale,
  fields: page.fields?.map(field => ({
    fieldId: field._id,
    label: field.label,
    value: `{${field.label}}`,
  })),
  customer: {
    loginToken: `{Login token}`,
    email: `{Email address}`,
  },
  files: page.files,
});

const subscriptionFallbackValues = (page, product) => ({
  _id: `{Payment ID}`,
  orderId: `{Order ID}`,
  createdAt: new Date(),
  status: 'active',
  currentPeriodEnd: new Date(),
  sku: product.sku,
  amount: product.price,
  livemode: true,
  interval: product.interval,
  intervalCount: product.intervalCount,
  planIterations: product.planIterations,
  setupFee: product.setupFee,
  currency: product.currency,
  locale: product.locale,
  fields: page.fields?.map(field => {
    return {
      fieldId: field._id,
      label: field.label,
      value: `{${field.label}}`,
    };
  }),
  variants: product.variants?.map(variant => ({
    name: variant.name,
    variantId: variant._id,
    selectedOptionName: `{${variant.name}}`,
    sku: `{${variant.name}}`,
  })),
  licenseKey: {
    key: '{License key}',
  },
  customer: {
    loginToken: `{Login token}`,
    email: `{Email address}`,
  },
  paymentMethod: {
    method: 'card',
    gateway: 'stripe',
    brand: 'visa',
    last4: '4242',
  },
  files: product.files,
  coupon: {
    code: '{Coupon code}',
    percentOff: 10,
    amountOff: 1000,
  },
});

const chargeFallbackValues = (page, product) => ({
  _id: `{Payment ID}`,
  orderId: `{Order ID}`,
  createdAt: new Date(),
  status: 'paid',
  sku: product.sku,
  amount: product.price,
  amountDue: 0,
  amountPaid: product.price,
  setupFee: product.setupFee,
  currency: product.currency,
  livemode: true,
  locale: product.locale,
  fields: page.fields?.map(field => {
    return {
      fieldId: field._id,
      label: field.label,
      value: `{${field.label}}`,
    };
  }),
  variants: product.variants?.map(variant => ({
    variantId: variant._id,
    name: variant.name,
    quantity: 1,
    ...((variant.selectionType === 'quantity' || variant.selectionType === 'multiple') && {
      selectedOptions: variant.options.map(option => ({
        optionId: option._id,
        optionName: option.name,
        additionalChargeAmount: option.additionalChargeAmount,
        originalAdditionalChargeAmount: option.discountedFromPrice,
        quantity: 1,
      })),
    }),
    ...(variant.selectionType === 'single' && {
      selectedOptionName: `{${variant.options[0].name}}`,
      additionalChargeAmount: variant.options[0].additionalChargeAmount,
      sku: `{${variant.options[0].sku}}`,
    }),

    sku: `{${variant.name}}`,
  })),
  licenseKey: {
    key: '{License key}',
  },
  customer: {
    loginToken: `{Login token}`,
    email: `{Email address}`,
  },
  paymentMethod: {
    method: 'card',
    gateway: 'stripe',
    brand: 'visa',
    last4: '4242',
  },
  files: product.files,
  coupon: {
    code: '{Coupon code}',
    discount: product.price * 0.1, // 10% of product price
  },
});

const eventChargeFallbackValues = page => ({
  _id: `{Payment ID}`,
  orderId: `{Order ID}`,
  createdAt: new Date(),
  status: 'paid',
  amount: 10000,
  amountDue: 0,
  amountPaid: 10000,
  currency: page.eventDetails?.currency,
  livemode: true,
  locale: page.locale,
  fields: page.fields?.map(field => {
    return {
      fieldId: field._id,
      label: field.label,
      value: `{${field.label}}`,
    };
  }),
  tickets:
    page.ticketGroups?.length > 0 && page.ticketGroups[0].ticketTypes?.length > 0
      ? [
          {
            ticketType: page.ticketGroups[0].ticketTypes[0]._id,
            name: page.ticketGroups[0].ticketTypes[0].name,
            quantity: 1,
          },
        ]
      : [],
  customer: {
    loginToken: `{Login token}`,
    email: `{Email address}`,
  },
  paymentMethod: {
    method: 'card',
    gateway: 'stripe',
    brand: 'visa',
    last4: '4242',
  },
  coupon: {
    code: '{Coupon code}',
    discount: 1000,
  },
});
